import React, { useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import technologies from '../data/technologies';
import { projects } from '../data/projects';
import { activities } from '../data/activities';
import { iconColors } from '../data/iconMapping';
import GitHubActivity from '../components/GitHubActivity';
import LeetCodeStats from '../components/LeetCodeStats';
import { Link } from 'react-router-dom';

const links = [
  { name: "Politecnico di Torino", url: "https://www.polito.it" },
  { name: "MSc Artificial Intelligence (UvA)", url: "https://www.uva.nl/shared-content/programmas/en/masters/artificial-intelligence/artificial-intelligence.html" },
  { name: "SmartData@Polito", url: "https://smartdata.polito.it" },
  { name: "PoliTOcean", url: "https://www.instagram.com/politocean/" },
  { name: "IEEE-HKN Mu Nu Chapter", url: "https://hknpolito.org" },
  { name: "Intraprendenti Program", url: "https://www.polito.it/en/education/aptitude-oriented-education/honors-programs/percorso-intraprendenti" },
  { name: "BEST", url: "https://www.best.eu.org/index.jsp" },
  { name: "Reply", url: "https://www.reply.com" },
];

function useFadeIn() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.classList.add('visible'); obs.disconnect(); }
    }, { threshold: 0.08 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

function FadeIn({ children, delay = 0, className = '' }) {
  const ref = useFadeIn();
  return (
    <div ref={ref} className={`fade-in ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

function Ring({ pct, size = 72, stroke = 3, color = 'var(--accent)' }) {
  const r = (size - stroke * 2) / 2;
  const circ = 2 * Math.PI * r;
  const dash = circ * (1 - pct / 100);
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', display: 'block' }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color}
        strokeWidth={stroke} strokeDasharray={circ} strokeDashoffset={dash} strokeLinecap="round" />
    </svg>
  );
}

function AboutMe() {
  const langs = [
    { name: "Italian", level: "Native", pct: 100 },
    { name: "English", level: "IELTS 7.5", pct: 85 },
  ];

  return (
    <FadeIn>
      <div className="card" data-section="about">
        <h2 className="section-title">About Me</h2>

        <div className="about-bio">
          <p>I'm an engineer with a foundation in <strong>Computer Engineering</strong> from{' '}
            <a href="https://www.polito.it" target="_blank" rel="noreferrer">Politecnico di Torino</a>.
            I am currently pursuing a <strong>Master's degree in Artificial Intelligence</strong> at the{' '}
            <a href="https://www.uva.nl" target="_blank" rel="noreferrer">University of Amsterdam (UvA)</a>,
            where I focus on machine learning, deep learning, and intelligent systems.
          </p>
          <p>In Amsterdam, my research has moved through hierarchical planning in world models (<strong>Hi-LeWM</strong>),
            layerwise probing of video foundation models, and the <strong>SIDReasoner</strong> reproducibility study.
            Check out the <Link to="/publications">Publications</Link> and <Link to="/projects">Projects</Link> pages for the details.</p>
          <p>During my time at <strong>SmartData@Polito</strong>, I worked on two projects involving RAG pipelines,
            LLM-based analysis, and data-centric methodologies for understanding economic phenomena and real-estate
            asset management.</p>
          <p>As part of the <strong>PoliTOcean</strong> robotics team, I contributed to ROS2 and PyQt6 development,
            creating real-time interfaces and control systems for underwater vehicles.</p>
          <p>I also served as <strong>Vice Coordinator</strong> for the Mu Nu Chapter of IEEE-HKN, where I worked
            in the Corporate Training area, organizing meetings for knowledge exchange and presenting insights to the
            chapter's associates.</p>
        </div>

        <div className="about-achievements">
          <p className="label-text">Achievements</p>
          <ul className="achievement-list">
            <li><span className="ach-dot" />1st place at the BEST Hackathon 2024 (Reale Group)</li>
            <li><span className="ach-dot" />2nd place at the IBEC BEST competition 2024 (Reply)</li>
            <li><span className="ach-dot" />1st place at ING's Global Hackathon 2026 — Student Track</li>
            <li><span className="ach-dot" />Admitted to the Intraprendenti program</li>
            <li><span className="ach-dot" />Selected for the Prosus AI Hackathon</li>
          </ul>
        </div>

        <div className="about-grid">
          <div className="about-block">
            <p className="label-text">Spoken Languages</p>
            <div className="lang-list">
              {langs.map(l => (
                <div key={l.name} className="lang-row">
                  <div className="lang-meta">
                    <span className="lang-name">{l.name}</span>
                    <span className="lang-level">{l.level}</span>
                  </div>
                  <div className="lang-track">
                    <div className="lang-fill" style={{ width: `${l.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="about-block">
            <p className="label-text">Technologies</p>
            <div className="tag-cloud">
              {technologies.map(t => {
                const color = iconColors[t] || '#666';
                return (
                  <span key={t} className="tech-tag" style={{ '--dot': color }}>
                    <span className="tech-dot" />{t}
                  </span>
                )
              })}
            </div>
          </div>
        </div>

        <div className="about-block" style={{ marginTop: 24 }}>
          <p className="label-text">Related Links</p>
          <div className="link-chips">
            {links.map(l => (
              <a key={l.name} href={l.url} target="_blank" rel="noreferrer" className="link-chip">
                {l.name} ↗
              </a>
            ))}
          </div>
        </div>
      </div>
    </FadeIn>
  );
}

function SkillsActivity() {
  const skillCounts = {};
  projects.forEach(p => {
    (p.technologies || []).forEach(t => { skillCounts[t] = (skillCounts[t] || 0) + 1; });
    (p.languages || []).forEach(t => { skillCounts[t] = (skillCounts[t] || 0) + 1; });
  });
  const skills = Object.entries(skillCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([name, count]) => ({ name, count }));

  const maxCount = skills.length > 0 ? skills[0].count : 1;

  const totalProjects = projects.length;
  const completedProjects = projects.filter(p => p.state === "Completed").length;
  const completionPct = totalProjects > 0 ? Math.round((completedProjects / totalProjects) * 100) : 0;

  return (
    <FadeIn delay={60}>
      <div className="card" data-section="skills">
        <h2 className="section-title">Skills & Activity</h2>

        <div className="subsection">
          <p className="label-text">Top Skills by Project Count</p>
          <div className="skill-bars">
            {skills.map((s, i) => (
              <div key={s.name} className="skill-row">
                <div className="skill-meta">
                  <span className="skill-name">{s.name}</span>
                  <span className="skill-count">{s.count} {s.count === 1 ? 'project' : 'projects'}</span>
                </div>
                <div className="skill-track">
                  <div
                    className="skill-fill"
                    style={{
                      width: `${(s.count / maxCount) * 100}%`,
                      background: 'var(--accent)'
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="subsection completion-row">
          <div className="completion-block">
            <p className="label-text">Project Completion</p>
            <div className="completion-card">
              <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                <Ring pct={completionPct} size={96} stroke={4} color="var(--accent)" />
                <div style={{ position: 'absolute', textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontWeight: 600, lineHeight: 1 }}>{completedProjects}/{totalProjects}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Done</div>
                </div>
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12, textAlign: 'center', lineHeight: 1.5 }}>
                Projects completed and<br />documented in this portfolio
              </p>
            </div>
          </div>

          <div className="activities-block">
            <p className="label-text">Other Activities</p>
            <div className="activities-grid">
              {activities.map(a => (
                <a key={a.id || a.name} href={a.link} className="activity-card" target="_blank" rel="noreferrer">
                  <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Ring pct={a.progress} size={52} stroke={3} color={a.progress === 100 ? 'rgba(255,255,255,0.6)' : (a.color || 'var(--accent)')} />
                    <div style={{ position: 'absolute', fontSize: 10, fontWeight: 600 }}>{a.progress}%</div>
                  </div>
                  <div className="activity-info">
                    <span className="activity-name">{a.name}</span>
                    <span className="activity-provider">{a.provider}</span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="subsection">
          <p className="label-text" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577v-2.165c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0 1 12 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.929.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.63-5.37-12-12-12z" /></svg>
            GitHub Contributions
            <span className="tooltip-wrapper">
              <span className="tooltip-icon">?</span>
              <span className="tooltip-popup">Only counts contributions to public repositories</span>
            </span>
          </p>
          <GitHubActivity />
        </div>

        <div className="subsection">
          <p className="label-text" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#FFA116"><path d="M13.483 0a1.374 1.374 0 0 0-.961.438L7.116 6.226l-3.854 4.126a5.266 5.266 0 0 0-1.209 2.104 5.35 5.35 0 0 0-.125.513 5.527 5.527 0 0 0 .062 2.362 5.83 5.83 0 0 0 .349 1.017 5.938 5.938 0 0 0 1.271 1.818l4.277 4.193.039.038c2.248 2.165 5.852 2.133 8.063-.074l2.396-2.392c.54-.54.54-1.414.003-1.955a1.378 1.378 0 0 0-1.951-.003l-2.396 2.392a3.021 3.021 0 0 1-4.205.038l-.02-.019-4.276-4.193c-.652-.64-.972-1.469-.948-2.263a2.68 2.68 0 0 1 .066-.523 2.545 2.545 0 0 1 .619-1.164L9.13 8.114c1.058-1.134 3.204-1.27 4.43-.278l3.501 2.831c.593.48 1.461.387 1.94-.207a1.384 1.384 0 0 0-.207-1.943l-3.5-2.831c-.8-.647-1.766-1.045-2.774-1.202l2.015-2.158A1.384 1.384 0 0 0 13.483 0zm-2.866 12.815a1.38 1.38 0 0 0-1.38 1.382 1.38 1.38 0 0 0 1.38 1.382H20.79a1.38 1.38 0 0 0 1.38-1.382 1.38 1.38 0 0 0-1.38-1.382z" /></svg>
            LeetCode Profile
          </p>
          <LeetCodeStats />
        </div>
      </div>
    </FadeIn>
  );
}

function GetInTouch() {
  return (
    <FadeIn delay={80}>
      <div className="card contact-card" data-section="contact">
        <h2 className="section-title">Get In Touch</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: 28, fontSize: 15 }}>
          Feel free to reach out for collaborations, opportunities, or just to connect.
        </p>
        <div className="contact-btns">
          <a href="mailto:salvatore.losardo@ikmail.com" className="contact-btn contact-btn--primary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
            Email
          </a>
          <a href="https://www.linkedin.com/in/salvatorels/" target="_blank" rel="noreferrer" className="contact-btn contact-btn--secondary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
            LinkedIn
          </a>
          <a href="https://github.com/SalvatoreLS" target="_blank" rel="noreferrer" className="contact-btn contact-btn--secondary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577v-2.165c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0 1 12 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.929.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.63-5.37-12-12-12z" /></svg>
            GitHub
          </a>
        </div>
      </div>
    </FadeIn>
  );
}

export default function Home() {
  return (
    <>
    <Helmet>
      <title>Salvatore Lo Sardo</title>
      <meta name="description" content="MSc Student in Artificial Intelligence at UvA. Passionate about Machine Learning, Deep Learning, Agentic AI, RAG pipelines, and Robotics. Previously at SmartData@Polito and PoliTOcean." />
      <meta property="og:title" content="Salvatore Lo Sardo" />
      <meta property="og:description" content="MSc Student in Artificial Intelligence at UvA. Passionate about Machine Learning, Deep Learning, Agentic AI, RAG pipelines, and Robotics." />
      <meta property="og:url" content="https://www.salvatore.losardo.eu/" />
      <link rel="canonical" href="https://www.salvatore.losardo.eu/" />
    </Helmet>
    <div className="page">
      <div className="container">
        <FadeIn>
          <div className="page-hero">
            <h1 className="page-hero-title">Welcome</h1>
            <p className="page-hero-sub">Computer engineer & AI student based in Amsterdam</p>
          </div>
        </FadeIn>
        <AboutMe />
        <SkillsActivity />
        <GetInTouch />
      </div>
    </div>
    </>
  );
}